import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { CustomerPaymentSheet } from "@/components/customer-payment-sheet";
import { EditCustomerDialog } from "@/components/edit-customer-dialog";
import { CustomerInfoDialog } from "@/components/customer-info-dialog";
import { Trash2, ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, Pencil, ShieldBan, ShieldCheck, User, ChevronDown, ChevronUp, Phone } from "lucide-react";

interface CustomerTableProps {
  customers: Customer[];
  products: Product[];
  jobs: string[];
  onDelete: (id: string) => void;
  onUpdateCustomer: (customer: Customer) => void;
  onAddProduct: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  onDeleteProduct: (id: string) => void;
  onUpdateProduct: (product: Product) => void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "settings") => void;
  mode?: "customers" | "payments"; // customers = بێ کلیک و بە دەستکاری، payments = بە کلیک و بێ دەستکاری
}

export function CustomerTable({ customers, products, jobs, onDelete, onUpdateCustomer, onAddProduct, onDeleteProduct, onUpdateProduct, onNavigate, mode = "payments" }: CustomerTableProps) {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [infoCustomer, setInfoCustomer] = useState<Customer | null>(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const toggleExpanded = (customerId: string) => {
    setExpandedCustomers(prev => {
      const newSet = new Set<string>();
      if (!prev.has(customerId)) {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setProfileOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleToggleBlock = (customer: Customer) => {
    onUpdateCustomer({
      ...customer,
      isBlocked: !customer.isBlocked
    });
  };

  const handleShowInfo = (customer: Customer) => {
    setInfoCustomer(customer);
    setInfoDialogOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        // سێیەم جار: لابردنی فلتەر
        setSortField(null);
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp className="h-3 w-3 text-primary" />
      : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue: string | number = "";
    let bValue: string | number = "";
    
    switch (sortField) {
      case "code":
        aValue = a.customerCode;
        bValue = b.customerCode;
        break;
      case "name":
        aValue = a.fullName;
        bValue = b.fullName;
        break;
      case "phone":
        aValue = a.phone1;
        bValue = b.phone1;
        break;
      case "guarantor":
        aValue = a.guarantorName;
        bValue = b.guarantorName;
        break;
      case "guarantorPhone":
        aValue = a.guarantorPhone;
        bValue = b.guarantorPhone;
        break;
      case "job":
        aValue = a.job;
        bValue = b.job;
        break;
      case "address":
        aValue = a.address;
        bValue = b.address;
        break;
    }
    
    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    }
    
    return sortDirection === "asc" 
      ? String(aValue).localeCompare(String(bValue), "ar")
      : String(bValue).localeCompare(String(aValue), "ar");
  });

  if (customers.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        هیچ کڕیارێک نییە
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="rounded-md border hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "code" ? "bg-muted/30" : ""}`} onClick={() => handleSort("code")}>
                <div className="flex items-center gap-1">
                  کۆد
                  <SortIcon field="code" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "name" ? "bg-muted/30" : ""}`} onClick={() => handleSort("name")}>
                <div className="flex items-center gap-1">
                  ناو
                  <SortIcon field="name" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "phone" ? "bg-muted/30" : ""}`} onClick={() => handleSort("phone")}>
                <div className="flex items-center gap-1">
                  مۆبایل
                  <SortIcon field="phone" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "guarantor" ? "bg-muted/30" : ""}`} onClick={() => handleSort("guarantor")}>
                <div className="flex items-center gap-1">
                  کەفیل
                  <SortIcon field="guarantor" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "guarantorPhone" ? "bg-muted/30" : ""}`} onClick={() => handleSort("guarantorPhone")}>
                <div className="flex items-center gap-1">
                  ژمارەی کەفیل
                  <SortIcon field="guarantorPhone" />
                </div>
              </TableHead>
              <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "job" ? "bg-muted/30" : ""}`} onClick={() => handleSort("job")}>
                <div className="flex items-center gap-1">
                  پیشە
                  <SortIcon field="job" />
                </div>
              </TableHead>
              {mode === "customers" && <TableHead className="text-right">دۆخ</TableHead>}
              {mode === "customers" && <TableHead className="text-right w-fit"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedCustomers.map((customer, index) => (
              <TableRow 
                key={customer.id} 
                className={`${mode === "payments" ? "cursor-pointer" : ""} ${index % 2 === 1 ? 'bg-muted/50' : ''}`}
                onClick={mode === "payments" ? () => handleViewCustomer(customer) : undefined}
              >
                <TableCell>
                  <Badge variant="outline">{customer.customerCode}</Badge>
                </TableCell>
                <TableCell className="font-medium">{customer.fullName}</TableCell>
                <TableCell dir="ltr" className="text-right">{customer.phone1}</TableCell>
                <TableCell>{customer.guarantorName || "-"}</TableCell>
                <TableCell dir="ltr" className="text-right">{customer.guarantorPhone || "-"}</TableCell>
                <TableCell>
                  {customer.job ? <Badge variant="secondary">{customer.job}</Badge> : "-"}
                </TableCell>
                {mode === "customers" && (
                  <TableCell>
                    {customer.isBlocked ? (
                      <Badge variant="destructive" className="text-xs">بلۆک کراو</Badge>
                    ) : (
                      <Badge variant="default" className="text-xs bg-green-600">چالاک</Badge>
                    )}
                  </TableCell>
                )}
                {mode === "customers" && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                        <MoreHorizontal className="h-4 w-4" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="text-right w-48">
                        <DropdownMenuItem onSelect={() => handleShowInfo(customer)}>
                          <User className="h-4 w-4 ml-2" />
                          زانیاری کڕیار
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => handleEditCustomer(customer)}>
                          <Pencil className="h-4 w-4 ml-2" />
                          دەستکاریکردن
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => handleToggleBlock(customer)}>
                          {customer.isBlocked ? (
                            <>
                              <ShieldCheck className="h-4 w-4 ml-2 text-green-600" />
                              <span className="text-green-600">لابردنی بلۆک</span>
                            </>
                          ) : (
                            <>
                              <ShieldBan className="h-4 w-4 ml-2 text-amber-600" />
                              <span className="text-amber-600">بلۆککردن</span>
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {(() => {
                          const customerProducts = products.filter(p => p.customerId === customer.id);
                          const hasProducts = customerProducts.length > 0;
                          return (
                            <DropdownMenuItem 
                              disabled={hasProducts}
                              onSelect={() => {
                                if (confirm("دڵنیایت لە سڕینەوەی ئەم کڕیارە؟")) {
                                  onDelete(customer.id);
                                }
                              }}
                              className={hasProducts ? "text-muted-foreground cursor-not-allowed" : "text-destructive focus:text-destructive"}
                            >
                              <Trash2 className="h-4 w-4 ml-2" />
                              {hasProducts ? "کاڵای لەسەر هەیە" : "سڕینەوە"}
                            </DropdownMenuItem>
                          );
                        })()}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {sortedCustomers.map((customer, index) => {
          const isExpanded = expandedCustomers.has(customer.id);
          return (
            <div 
              key={customer.id} 
              className={`rounded-xl border bg-card overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${index % 2 === 1 ? 'bg-muted/60' : ''}`}
            >
              {/* Main Row - Always Visible */}
              <div 
                className="p-3 flex items-center gap-3 cursor-pointer"
                onClick={() => mode === "payments" ? handleViewCustomer(customer) : toggleExpanded(customer.id)}
              >
                {/* Avatar with Code */}
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">{customer.customerCode}</span>
                </div>
                
                {/* Name and Phone */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{customer.fullName}</span>
                    {customer.isBlocked ? (
                      <Badge variant="destructive" className="text-[10px] px-1.5 h-4">بلۆک</Badge>
                    ) : (
                      <Badge variant="default" className="text-[10px] px-1.5 h-4 bg-green-600">چالاک</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Phone className="h-3 w-3" />
                    <span dir="ltr">{customer.phone1}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {mode === "customers" && (
                    <>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(customer.id);
                        }}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg border bg-muted/50 hover:bg-muted cursor-pointer">
                            <MoreHorizontal className="h-4 w-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent alignOffset={-50} className="text-right w-48">
                          <DropdownMenuItem onSelect={() => handleShowInfo(customer)}>
                            <User className="h-4 w-4 ml-2" />
                            زانیاری کڕیار
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => handleEditCustomer(customer)}>
                            <Pencil className="h-4 w-4 ml-2" />
                            دەستکاریکردن
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => handleToggleBlock(customer)}>
                            {customer.isBlocked ? (
                              <>
                                <ShieldCheck className="h-4 w-4 ml-2 text-green-600" />
                                <span className="text-green-600">لابردنی بلۆک</span>
                              </>
                            ) : (
                              <>
                                <ShieldBan className="h-4 w-4 ml-2 text-amber-600" />
                                <span className="text-amber-600">بلۆککردن</span>
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {(() => {
                            const customerProducts = products.filter(p => p.customerId === customer.id);
                            const hasProducts = customerProducts.length > 0;
                            return (
                              <DropdownMenuItem 
                                disabled={hasProducts}
                                onSelect={() => {
                                  if (confirm("دڵنیایت لە سڕینەوەی ئەم کڕیارە؟")) {
                                    onDelete(customer.id);
                                  }
                                }}
                                className={hasProducts ? "text-muted-foreground cursor-not-allowed" : "text-destructive focus:text-destructive"}
                              >
                                <Trash2 className="h-4 w-4 ml-2" />
                                {hasProducts ? "کاڵای لەسەر هەیە" : "سڕینەوە"}
                              </DropdownMenuItem>
                            );
                          })()}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {mode === "customers" && isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                  <div className="space-y-2 pt-3">
                    {customer.phone2 && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">مۆبایل ٢</span>
                        <span className="font-medium text-sm" dir="ltr">{customer.phone2}</span>
                      </div>
                    )}
                    {customer.job && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">پیشە</span>
                        <span className="font-medium text-sm">{customer.job}</span>
                      </div>
                    )}
                    {customer.guarantorName && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">کەفیل</span>
                        <span className="font-medium text-sm">{customer.guarantorName}</span>
                      </div>
                    )}
                    {customer.guarantorPhone && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">ژمارەی کەفیل</span>
                        <span className="font-medium text-sm" dir="ltr">{customer.guarantorPhone}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                        <span className="text-muted-foreground text-xs">ناونیشان</span>
                        <span className="font-medium text-sm">{customer.address}</span>
                      </div>
                    )}
                    {customer.notes && (
                      <div className="pt-2 mt-1 border-t">
                        <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                        <p className="text-sm bg-muted/50 rounded-lg p-2 text-muted-foreground">{customer.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <CustomerPaymentSheet
        customer={selectedCustomer}
        products={products}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onDelete={onDelete}
        onAddProduct={onAddProduct}
        onDeleteProduct={onDeleteProduct}
        onUpdateProduct={onUpdateProduct}
        onNavigate={onNavigate}
      />

      {/* Edit Customer Dialog - triggered from dropdown */}
      {editCustomer && (
        <EditCustomerDialog 
          customer={editCustomer} 
          onUpdate={(updatedCustomer) => {
            onUpdateCustomer(updatedCustomer);
            setEditDialogOpen(false);
            setEditCustomer(null);
          }} 
          jobs={jobs}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setEditCustomer(null);
          }}
        />
      )}

      {/* Customer Info Dialog - triggered from dropdown */}
      <CustomerInfoDialog
        customer={infoCustomer}
        open={infoDialogOpen}
        onOpenChange={(open) => {
          setInfoDialogOpen(open);
          if (!open) setInfoCustomer(null);
        }}
      />
    </>
  );
}
